"use client";

import React from "react";
import type { INewsItem } from "../../../interfaces/News.interface";
import type { NewsAEMTrending } from "../../../interfaces/NewsAEM.interface";
import VerticalNewsCard from "../components/VerticalNewsCard";
import NewsEmptyState from "../components/NewsEmptyState";
import BFLApiFailure from "@repo/shared-ui/BFLApiFailure";
import type { INewsFilters } from "../../../interfaces/NewsFilter.interface";
import { stringToBoolean } from "@repo/shared-utils/pwa-utils";
import BFLLink from "@repo/shared-ui/BFLLink";
import { useHorizontalScroll } from "../hooks/useHorizontalScroll";
import { trackComponent } from "@repo/analytics";
import {
  componentNameConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import { getCustomerIdForAnalytics } from "../../../utils/analytics-utils";
import {
  campaignName,
  journeyTag,
  productCategory,
  propertyType,
  referenceId,
  sprint,
  subSection,
} from "../../../pages/News/newsTrackingConstants";
import { trackFailureScenarioRetryClick } from "../components/trackFailureScenarioRetry";

interface TrendingProps {
  trendingNews: INewsItem[];
  aemContent?: NewsAEMTrending | null;
  isLoading?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
  componentPosition?: string;
  filters?: INewsFilters;
}

const Trending: React.FC<TrendingProps> = ({
  trendingNews,
  aemContent,
  isLoading = false,
  hasError = false,
  onRetry,
  componentPosition,
  filters = { events: [], index: [], instrument: [], marketCap: [], sector: [] },
}) => {
  const { scrollRef, showLeftButton, showRightButton, scrollNext, scrollPrev } =
    useHorizontalScroll([trendingNews]);

  const handleViewAllClick = async () => {
    const customerId = await getCustomerIdForAnalytics();

    await trackComponent({
      componentName: componentNameConstants.IPO_NEWS,
      sectionTitle: componentNameConstants.TRENDING_NEWS,
      eventType: dlEventConstants.IPO_NEWS_LINK_CLICK,
      ctaText: aemContent?.viewAllText ?? "View all",
      productCategory,
      subSection: aemContent?.componentTitle ?? subSection,
      cleverTapOverrides: {
        EP_JOURNEY: journeyTag,
        EP_PROPERTY_TYPE: propertyType,
        EP_PRODUCT_NAME: subSection,
        EP_CAMPAIGN: campaignName,
        EP_SPRINT: sprint,
        EP_REFERENCE_ID: referenceId,
        ...(customerId && { EP_CUSTOMER_ID: customerId }),
        EP_SR_NUMBER: componentPosition ?? "2",
      },
    });
  };

  const shouldShowViewAll = () => {
    if (typeof aemContent?.showViewAll === "boolean") {
      return aemContent.showViewAll;
    }
    if (typeof aemContent?.showViewAll === "string") {
      return stringToBoolean(aemContent.showViewAll);
    }
    return false;
  };

  // Render section header component
  const renderHeader = () => (
    <div className="mb-[12px]">
      <h2 className="font-rubik mb-[4px] text-[18px] font-medium leading-[100%] tracking-[0px] text-[#1A1A1A] md:mb-[8px] md:text-[20px]">
        {aemContent?.componentTitle ?? "Trending"}
      </h2>
      <p className="font-rubik align-middle text-[12px] font-normal leading-[100%] tracking-normal text-gray-600 md:text-[14px] md:leading-[20px]">
        {aemContent?.subTitle ?? "Get real time updates about trending stocks"}
      </p>
    </div>
  );

  // Render content based on state
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="px-4 py-3">
          <div className="mb-[12px]">
            <div className="h-6 w-32 rounded bg-gray-200 animate-pulse mb-2" />
            <div className="h-4 w-64 rounded bg-gray-200 animate-pulse" />
          </div>
          <div className="flex gap-3">
            {["card-1", "card-2", "card-3"].map((id) => (
              <div
                key={id}
                className="w-[200px] h-[268px] rounded bg-gray-200 animate-pulse"
              />
            ))}
          </div>
        </div>
      );
    }

    if (hasError) {
      return (
        <div className="flex min-h-[400px] items-center justify-center py-12">
          <BFLApiFailure
            imageSrc={aemContent?.images?.errorImage ?? ""}
            imageAlt={aemContent?.errorImageAlt ?? "Error"}
            error={
              aemContent?.errorTitle ??
              "Unable to fetch the information at this time"
            }
            errorDescription={
              aemContent?.errorDescription ?? "Please try again later"
            }
            retryButtonText={aemContent?.errorRetryButtonText ?? "Retry"}
            onRetry={onRetry}
            trackingConfig={{
              sectionTitle: `${aemContent?.componentTitle ?? "News"} unable to load`,
              componentPosition: componentPosition ?? "2",
              entryPoint: "no_result_found",
              productCategory,
              subSection,
              journeyTag,
              campaignName,
              sprint,
              referenceId,
            }}
            onRetryClick={(params) =>
              trackFailureScenarioRetryClick(params, getCustomerIdForAnalytics)
            }
          />
        </div>
      );
    }

    if (trendingNews.length === 0) {
      return (
        <div className="px-4 py-4">
          <NewsEmptyState
            filters={filters}
            title={aemContent?.emptyStateTitle ?? "No results found"}
            description={
              aemContent?.emptyStateDescription ??
              "Your current filter selection did not match any results."
            }
            emptyDescription={
              aemContent?.emptyStateDescription ??
              "No trending news available at the moment."
            }
            aemImages={aemContent?.images}
          />
        </div>
      );
    }

    return (
      <div className="relative -mx-4 px-4">
        <div className="overflow-hidden">
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {trendingNews.map((item) => (
              <VerticalNewsCard
                key={item.id}
                newsItem={item}
                showCompanies={true}
                showTimestamp={false}
                aemImages={aemContent?.images}
                timestampLabels={aemContent?.timestampLabels}
                newsType="trending"
                componentPosition={componentPosition}
              />
            ))}
          </div>
        </div>
        {showLeftButton && (
          <button
            onClick={scrollPrev}
            className="z-4 absolute left-0 top-1/2 hidden h-[48px] w-[42px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-br-[8px] rounded-tr-[8px] border border-[#E4E7EC] bg-white/95 pb-2 pl-1 pr-1.5 pt-2 shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-150 hover:scale-[1.02] hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff6b35] md:flex"
            aria-label={
              aemContent?.scrollLeftAriaLabel ?? "Scroll trending news left"
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="h-6 w-6 text-[#ff6b35]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        )}
        {showRightButton && (
          <button
            onClick={scrollNext}
            className="z-4 absolute right-0 top-1/2 hidden h-[48px] w-[42px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-bl-[8px] rounded-tl-[8px] border border-[#E4E7EC] bg-white/95 pb-2 pl-1.5 pr-1 pt-2 shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-150 hover:scale-[1.02] hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff6b35] md:flex"
            aria-label={
              aemContent?.scrollRightAriaLabel ?? "Scroll trending news right"
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="h-6 w-6 text-[#ff6b35]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="px-4 py-3">
      {renderHeader()}
      {renderContent()}
      {shouldShowViewAll() && (
        <BFLLink openInNewTab href={aemContent?.redirectLink ?? ""}>
          <div className="mt-[8px] max-w-fit cursor-pointer border-0 bg-transparent px-0 pb-[4px] pt-[8px] text-left transition-opacity duration-200 ease-linear">
            <button
              type="button"
              className="text-link-primary cursor-pointer text-[14px] font-medium"
              onClick={handleViewAllClick}
            >
              {aemContent?.viewAllText ?? "View all"}
            </button>
          </div>
        </BFLLink>
      )}
    </div>
  );
};

export default Trending;
